/**
 * Copyright Highway9 Networks Inc.
 *
 * Redux slice powering the AI assistant chat panel. Each user's browser
 * keeps its own slice; when the panel is open the WebSocket bridge
 * pushes streaming events into it. Multiple sessions can be open in
 * parallel because every action is keyed by `sessionID`.
 */

import { PayloadAction, createSelector, createSlice } from "@reduxjs/toolkit";
import {
  DEFAULT_PRIMARY_AGENT_CANONICAL,
  filterAgentsVisibleForUi,
  isAiAssistantAgentHiddenFromUi,
  normalizeAiAssistantSession,
  sessionParentId,
  type AiAssistantAgent,
  type AiAssistantMessage,
  type AiAssistantMessagePart,
  type AiAssistantQuestionRequest,
  type AiAssistantSession,
} from "@h9/ui-lib/components/organisms/AiAssistant";
import { RootState } from "..";
import { currentAssistantUserRef } from "~/services/ai-assistant-service";

/** True if parts contain any non-empty text or non-text chunks (tool, reasoning, ...). */
export function hasRenderableParts(parts: AiAssistantMessagePart[] | undefined): boolean {
  if (!parts?.length) return false;
  for (const p of parts) {
    const any = p as any;
    if (any.type === "step-start" || any.type === "step-finish") continue;
    if (any.type === "text" || any.type === undefined || any.type === null) {
      if (String(any.text ?? any.content ?? "").trim().length > 0) return true;
      continue;
    }
    return true;
  }
  return false;
}

// Part helpers now live in @h9/ui-lib so Max shares them; re-exported so every existing
// importer of this slice is unchanged.
export {
  filterRenderableParts,
  isTerminalToolStatus,
  toolPartIsRunning,
  partsHaveRunningTool,
} from "@h9/ui-lib/components/organisms/AiAssistant";


/** Stable key for user bubbles (optimistic + server) for placeholder replacement. */
function canonicalUserTextFromMessage(m: AiAssistantMessage): string {
  const parts = m.parts ?? [];
  const chunks: string[] = [];
  for (const p of parts) {
    const any = p as any;
    if (any.type === "step-start" || any.type === "step-finish") continue;
    if (any.type === "text" || any.type === undefined || any.type === null) {
      const t = String(any.text ?? any.content ?? "").trim();
      if (t) chunks.push(t);
      continue;
    }
    if (any.type === "file") {
      const url = String(any.url ?? "");
      chunks.push(
        `file:${String(any.filename ?? "")}:${String(any.mime ?? "")}:${url.length}:${url.slice(0, 96)}`
      );
    }
  }
  return chunks.join("\n").trim();
}

/**
 * `appendUserMessage` uses `local_*` ids; the server later commits the same turn with a
 * real message id. Replace the placeholder instead of pushing a duplicate user row.
 */
function findLocalUserPlaceholderIndex(
  messages: AiAssistantMessage[],
  merged: AiAssistantMessage
): number {
  if ((merged.info.role ?? "assistant") !== "user") return -1;
  const want = canonicalUserTextFromMessage(merged);
  if (!want) return -1;
  return messages.findIndex((m) => {
    const id = m.info.id;
    if (id == null || !String(id).startsWith("local_")) return false;
    if ((m.info.role ?? "assistant") !== "user") return false;
    return canonicalUserTextFromMessage(m) === want;
  });
}

/** Bump both updatedAt and time.updated — sort reads time.updated first after list sync. */
function touchSessionUpdated(session: AiAssistantSession): void {
  const now = Date.now();
  session.updatedAt = now;
  const prev =
    session.time && typeof session.time === "object" && !Array.isArray(session.time)
      ? (session.time as Record<string, unknown>)
      : {};
  session.time = { ...prev, updated: now };
}

function mergeStreamingPart(prev: AiAssistantMessagePart, next: AiAssistantMessagePart): AiAssistantMessagePart {
  const a = prev as any;
  const b = next as any;
  const merged: any = { ...a, ...b };
  const textish =
    (merged.type === "text" || merged.type === undefined) &&
    (a.type === "text" || a.type === undefined || !a.type);
  if (!textish) {
    return merged as AiAssistantMessagePart;
  }
  const delta = b.delta ?? b.textDelta;
  if (typeof delta === "string" && delta.length > 0) {
    const base = String(a.text ?? a.content ?? "");
    merged.text = base + delta;
  }
  const prevText = String(a.text ?? a.content ?? "");
  const nextText = b.text;
  if (
    (nextText === "" || nextText === undefined) &&
    prevText.length > 0 &&
    (b.content === undefined || b.content === "")
  ) {
    merged.text = a.text ?? a.content ?? prevText;
  }
  if (typeof nextText === "string" && nextText.length > prevText.length) {
    merged.text = nextText;
  }
  return merged as AiAssistantMessagePart;
}

export type SessionStatus = "idle" | "thinking" | "streaming" | "error";

export type EmbeddedSessionLink = {
  parentSessionId: string;
  taskMessageId?: string;
  taskPartId?: string;
};

export type SessionState = {
  session: AiAssistantSession;
  messages: AiAssistantMessage[];
  // While a turn is in flight we accumulate streaming parts here keyed
  // by message ID so the UI can render them as they arrive.
  streamingParts: Record<string, AiAssistantMessagePart[]>;
  status: SessionStatus;
  errorText?: string;
};

type AiAssistantState = {
  panelOpen: boolean;
  panelWidth: number;
  connected: boolean;
  email?: string;
  agents: AiAssistantAgent[];
  selectedAgent?: string;
  selectedModel?: string;
  sessionOrder: string[];
  sessions: Record<string, SessionState>;
  /** Task child sessions shown inline under a parent chat, not in the session switcher. */
  embeddedByChildId: Record<string, EmbeddedSessionLink>;
  activeSessionId?: string;
  globalError?: string;
  /** OpenCode `question.asked` requests awaiting user reply, keyed by request id. */
  pendingQuestions: Record<string, AiAssistantQuestionRequest & { answering?: boolean }>;
};

const initialState: AiAssistantState = {
  panelOpen: false,
  panelWidth: 800,
  connected: false,
  agents: [],
  sessionOrder: [],
  sessions: {},
  embeddedByChildId: {},
  pendingQuestions: {},
};

const slice = createSlice({
  name: "aiAssistant",
  initialState,
  reducers: {
    setPanelOpen(state, action: PayloadAction<boolean>) {
      state.panelOpen = action.payload;
    },
    togglePanel(state) {
      state.panelOpen = !state.panelOpen;
    },
    setPanelWidth(state, action: PayloadAction<number>) {
      const min = 320;
      const max = 1100;
      state.panelWidth = Math.max(min, Math.min(max, Math.round(action.payload)));
    },
    setConnected(state, action: PayloadAction<{ connected: boolean; email?: string }>) {
      state.connected = action.payload.connected;
      if (action.payload.email !== undefined) state.email = action.payload.email;
    },
    setAgents(state, action: PayloadAction<AiAssistantAgent[]>) {
      const agents = filterAgentsVisibleForUi(action.payload);
      state.agents = agents;
      if (
        state.selectedAgent &&
        isAiAssistantAgentHiddenFromUi(state.selectedAgent)
      ) {
        state.selectedAgent = undefined;
      }
      if (!state.selectedAgent && agents.length > 0) {
        const primary =
          agents.find(
            (a) => a.mode === "primary" && a.name === DEFAULT_PRIMARY_AGENT_CANONICAL
          ) ??
          agents.find((a) => a.mode === "primary") ??
          agents[0];
        state.selectedAgent = primary?.name;
        state.selectedModel = primary?.model;
      }
    },
    setSelectedAgent(state, action: PayloadAction<string | undefined>) {
      state.selectedAgent = action.payload;
      const agent = state.agents.find((a) => a.name === action.payload);
      if (agent?.model) state.selectedModel = agent.model;
    },
    setSelectedModel(state, action: PayloadAction<string | undefined>) {
      state.selectedModel = action.payload;
    },
    setActiveSession(state, action: PayloadAction<string | undefined>) {
      state.activeSessionId = action.payload;
    },
    registerEmbeddedSession(
      state,
      action: PayloadAction<{
        childSessionId: string;
        parentSessionId: string;
        taskMessageId?: string;
        taskPartId?: string;
        session?: AiAssistantSession;
      }>
    ) {
      const { childSessionId, parentSessionId, taskMessageId, taskPartId, session } =
        action.payload;
      if (!childSessionId || !parentSessionId || childSessionId === parentSessionId) return;

      state.embeddedByChildId[childSessionId] = {
        parentSessionId,
        taskMessageId,
        taskPartId,
      };

      if (!state.sessions[childSessionId]) {
        state.sessions[childSessionId] = {
          session: session ?? { id: childSessionId, parentId: parentSessionId },
          messages: [],
          streamingParts: {},
          status: "streaming",
        };
      } else if (session) {
        state.sessions[childSessionId].session = {
          ...state.sessions[childSessionId].session,
          ...session,
        };
      }

      state.sessionOrder = state.sessionOrder.filter((id) => id !== childSessionId);
      if (state.activeSessionId === childSessionId) {
        state.activeSessionId = parentSessionId;
      }
    },
    upsertSession(state, action: PayloadAction<AiAssistantSession>) {
      const patch = normalizeAiAssistantSession(action.payload, action.payload?.id);
      if (!patch?.id) return;
      const id = patch.id;

      const parentId = sessionParentId(patch);
      if (parentId) {
        state.embeddedByChildId[id] = state.embeddedByChildId[id] ?? { parentSessionId: parentId };
      }
      const isEmbedded = Boolean(state.embeddedByChildId[id] || parentId);
      const isHidden = patch.isHidden === true;

      if (!state.sessions[id]) {
        state.sessions[id] = {
          session: patch,
          messages: [],
          streamingParts: {},
          status: "idle",
        };
        if (!isEmbedded && !isHidden) {
          state.sessionOrder.unshift(id);
        }
      } else {
        state.sessions[id].session = { ...state.sessions[id].session, ...patch };
      }

      if (isHidden) {
        state.sessionOrder = state.sessionOrder.filter((sid) => sid !== id);
      }

      if (isEmbedded) {
        state.sessionOrder = state.sessionOrder.filter((sid) => sid !== id);
        if (state.activeSessionId === id && parentId) {
          state.activeSessionId = parentId;
        }
      }
    },
    setSessionMessages(
      state,
      action: PayloadAction<{ sessionID: string; messages: AiAssistantMessage[] }>
    ) {
      const s = state.sessions[action.payload.sessionID];
      if (!s) return;
      s.messages = action.payload.messages;
      s.streamingParts = {};
    },
    appendUserMessage(
      state,
      action: PayloadAction<{
        sessionID: string;
        text?: string;
        parts?: AiAssistantMessage["parts"];
        localId?: string;
      }>
    ) {
      const s = state.sessions[action.payload.sessionID];
      if (!s) return;
      const selfUser = currentAssistantUserRef();
      const parts =
        action.payload.parts && action.payload.parts.length > 0
          ? action.payload.parts
          : action.payload.text
            ? [{ type: "text" as const, text: action.payload.text }]
            : [];
      if (!parts.length) return;
      s.messages.push({
        info: {
          id: action.payload.localId ?? `local_${Date.now()}`,
          role: "user",
          sessionID: action.payload.sessionID,
          time: { created: Date.now() },
        },
        parts,
        ...(selfUser ? { user: selfUser } : {}),
      });
      touchSessionUpdated(s.session);
      s.status = "thinking";
    },
    setSessionStatus(
      state,
      action: PayloadAction<{ sessionID: string; status: SessionStatus; errorText?: string }>
    ) {
      const s = state.sessions[action.payload.sessionID];
      if (!s) return;
      s.status = action.payload.status;
      s.errorText = action.payload.errorText;
    },
    receiveStreamingPart(
      state,
      action: PayloadAction<{
        sessionID: string;
        messageID: string;
        part: AiAssistantMessagePart;
      }>
    ) {
      let s = state.sessions[action.payload.sessionID];
      if (!s) {
        const link = state.embeddedByChildId[action.payload.sessionID];
        if (!link) return;
        s = {
          session: { id: action.payload.sessionID, parentId: link.parentSessionId },
          messages: [],
          streamingParts: {},
          status: "streaming",
        };
        state.sessions[action.payload.sessionID] = s;
      }
      const list = s.streamingParts[action.payload.messageID] ?? [];
      const idxKey =
        (action.payload.part as any).id ??
        (action.payload.part as any).index ??
        list.length;
      const existing = list.findIndex(
        (p) => ((p as any).id ?? (p as any).index ?? -1) === idxKey
      );
      if (existing >= 0) {
        list[existing] = mergeStreamingPart(list[existing], action.payload.part);
      } else {
        list.push(action.payload.part);
      }
      s.streamingParts[action.payload.messageID] = list;
      if (s.status !== "streaming") s.status = "streaming";
    },
    completeStreamingMessage(
      state,
      action: PayloadAction<{ sessionID: string; message: AiAssistantMessage }>
    ) {
      let s = state.sessions[action.payload.sessionID];
      if (!s) {
        const link = state.embeddedByChildId[action.payload.sessionID];
        if (!link) return;
        s = {
          session: { id: action.payload.sessionID, parentId: link.parentSessionId },
          messages: [],
          streamingParts: {},
          status: "idle",
        };
        state.sessions[action.payload.sessionID] = s;
      }
      const messageID = action.payload.message?.info?.id;
      if (!messageID) return;

      const streamed = s.streamingParts[messageID];
      let parts = action.payload.message.parts ?? [];
      const apiOk = hasRenderableParts(parts);
      const streamOk = hasRenderableParts(streamed);
      if (!apiOk && streamOk && streamed) {
        parts = streamed;
      }
      if (messageID && streamed !== undefined) {
        delete s.streamingParts[messageID];
      }
      let merged: AiAssistantMessage = { ...action.payload.message, parts };
      const idx = s.messages.findIndex((m) => m.info.id === merged.info.id);
      if (idx >= 0) {
        const prev = s.messages[idx];
        // SSE can emit message.updated with empty parts after server.message_complete
        // already committed the full transcript — do not wipe the UI bubble.
        if (!hasRenderableParts(merged.parts) && hasRenderableParts(prev.parts)) {
          merged = { ...merged, parts: prev.parts };
        }
        if (!merged.user && prev.user) {
          merged = { ...merged, user: prev.user };
        }
        s.messages[idx] = merged;
      } else {
        if (!hasRenderableParts(merged.parts)) {
          return;
        }
        const localIdx = findLocalUserPlaceholderIndex(s.messages, merged);
        if (localIdx >= 0) {
          const prev = s.messages[localIdx];
          if (!merged.user && prev.user) {
            merged = { ...merged, user: prev.user };
          }
          if ((merged.info.role ?? "assistant") === "user" && !merged.user) {
            const selfUser = currentAssistantUserRef();
            if (selfUser) merged = { ...merged, user: selfUser };
          }
          s.messages[localIdx] = merged;
        } else {
          if ((merged.info.role ?? "assistant") === "user" && !merged.user) {
            const selfUser = currentAssistantUserRef();
            if (selfUser) merged = { ...merged, user: selfUser };
          }
          s.messages.push(merged);
        }
      }
      s.status = "idle";
      s.errorText = undefined;
      touchSessionUpdated(s.session);
    },
    /** Replace top-level sessions from a scoped list fetch (keeps embedded task children). */
    syncSessionList(state, action: PayloadAction<{ sessions: AiAssistantSession[] }>) {
      const normalized = action.payload.sessions
        .map((s) => normalizeAiAssistantSession(s, s?.id))
        .filter((s): s is AiAssistantSession => Boolean(s?.id));
      const incomingIds = new Set(normalized.map((s) => s.id));

      for (const id of [...state.sessionOrder]) {
        if (state.embeddedByChildId[id]) continue;
        if (!incomingIds.has(id)) {
          delete state.sessions[id];
          state.sessionOrder = state.sessionOrder.filter((sid) => sid !== id);
          if (state.activeSessionId === id) state.activeSessionId = undefined;
        }
      }

      // WHY: rebuild order from the list payload. Unshifting only *new* ids left stale
      // positions and reversed batches (API newest-first → unshift → oldest-first).
      const nextOrder: string[] = [];
      for (const session of normalized) {
        const id = session.id;
        const parentId = sessionParentId(session);
        if (parentId) {
          state.embeddedByChildId[id] =
            state.embeddedByChildId[id] ?? { parentSessionId: parentId };
        }

        if (!state.sessions[id]) {
          state.sessions[id] = {
            session,
            messages: [],
            streamingParts: {},
            status: "idle",
          };
        } else {
          state.sessions[id].session = { ...state.sessions[id].session, ...session };
        }

        const isEmbedded = Boolean(state.embeddedByChildId[id] || parentId);
        if (isEmbedded) {
          if (state.activeSessionId === id && parentId) {
            state.activeSessionId = parentId;
          }
          continue;
        }
        nextOrder.push(id);
      }
      state.sessionOrder = nextOrder;
    },
    removeSession(state, action: PayloadAction<string>) {
      const id = action.payload;
      for (const [childId, link] of Object.entries(state.embeddedByChildId)) {
        if (childId === id || link.parentSessionId === id) {
          delete state.sessions[childId];
          delete state.embeddedByChildId[childId];
        }
      }
      for (const [qid, q] of Object.entries(state.pendingQuestions)) {
        if (q.sessionID === id) delete state.pendingQuestions[qid];
      }
      delete state.sessions[id];
      delete state.embeddedByChildId[id];
      state.sessionOrder = state.sessionOrder.filter((sid) => sid !== id);
      if (state.activeSessionId === id) {
        state.activeSessionId = undefined;
      }
    },
    setGlobalError(state, action: PayloadAction<string | undefined>) {
      state.globalError = action.payload;
    },
    upsertPendingQuestion(state, action: PayloadAction<AiAssistantQuestionRequest>) {
      const q = action.payload;
      if (!q.id || !q.sessionID) return;
      state.pendingQuestions[q.id] = {
        ...q,
        answering: state.pendingQuestions[q.id]?.answering,
      };
    },
    syncPendingQuestionsForSession(
      state,
      action: PayloadAction<{ sessionID: string; questions: AiAssistantQuestionRequest[] }>
    ) {
      const { sessionID, questions } = action.payload;
      for (const [id, q] of Object.entries(state.pendingQuestions)) {
        if (q.sessionID === sessionID) delete state.pendingQuestions[id];
      }
      for (const q of questions) {
        if (q.id && q.sessionID === sessionID) {
          state.pendingQuestions[q.id] = q;
        }
      }
    },
    setPendingQuestionAnswering(
      state,
      action: PayloadAction<{ requestID: string; answering: boolean }>
    ) {
      const q = state.pendingQuestions[action.payload.requestID];
      if (!q) return;
      q.answering = action.payload.answering;
    },
    removePendingQuestion(state, action: PayloadAction<{ requestID: string }>) {
      delete state.pendingQuestions[action.payload.requestID];
    },
    reset(state) {
      state.connected = false;
      state.sessionOrder = [];
      state.sessions = {};
      state.embeddedByChildId = {};
      state.activeSessionId = undefined;
      state.pendingQuestions = {};
    },
  },
});

export const aiAssistantActions = slice.actions;
export default slice.reducer;

export const selectAiAssistant = (state: RootState) => state.aiAssistant;
export const selectActiveSession = (state: RootState) => {
  const id = state.aiAssistant.activeSessionId;
  return id ? state.aiAssistant.sessions[id] : undefined;
};
export const selectPanelOpen = (state: RootState) => state.aiAssistant.panelOpen;
export const selectPanelWidth = (state: RootState) => state.aiAssistant.panelWidth;
// Stable results - fresh arrays fail useSelector's reference check and
// re-render the always-mounted ChatPanel on every dispatch (MCS-4316)
const EMPTY_PENDING: never[] = [];
const selectPendingQuestionsBySession = createSelector(
  [(state: RootState) => state.aiAssistant.pendingQuestions],
  (pendingQuestions) => {
    const bySession: Record<string, (AiAssistantQuestionRequest & { answering?: boolean })[]> = {};
    for (const question of Object.values(pendingQuestions)) {
      (bySession[question.sessionID] ??= []).push(question);
    }
    return bySession;
  },
);
export const selectPendingQuestionsForSession = (sessionID: string | undefined) => (state: RootState) =>
  (sessionID ? selectPendingQuestionsBySession(state)[sessionID] : undefined) ?? EMPTY_PENDING;
export const selectHasPendingQuestionForSession = (sessionID: string | undefined) => (state: RootState) =>
  selectPendingQuestionsForSession(sessionID)(state).length > 0;
